import { useQuery } from "@apollo/client";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import BrazilianIpesContent from "components/company/v3/brazilian/contents/events/BrazilianIpesContent";
import RightsIssuesContent from "components/company/v3/contents/events/RightsIssuesContent";
import StockSplitContent from "components/company/v3/contents/events/StockSplitContent";
import CalendarContent from "components/company/v3/contents/events/CalendarContent";
import DividendsContent from "components/company/v3/contents/events/DividendsContent";
import StockDividendsContent from "components/company/v3/contents/events/StockDividendsContent";
import { CompanyEventsTabEnum } from "components/company/v3/menu/enums";
import type { EventsCalendarState } from "components/investorrelations/EventsCalendarLocalState";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Tabs } from "components/ui/Tabs";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import GlobalCorporateActionsQuery from "lib/queries/GlobalCorporateActionsQuery";
import IssuerDividendsQuery from "lib/queries/IssuerDividendsQuery";
import type {
  IGlobalCorporateActionsQuery,
  IGlobalCorporateActionsQueryVariables,
} from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import type {
  IIssuerDividendsQuery,
  IIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/IssuerDividendsQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import {
  CalendarDaysIcon,
  CirclePercentIcon,
  HandCoinsIcon,
  MegaphoneIcon,
  SquareSplitHorizontalIcon,
  TicketPercentIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { IIssuerType, ISecurityCountry } from "types/graphqlTypes";

export type EventsBoxContext = "FIIS" | "ACOES" | "STOCKS";

const CONTEXT_CONFIG: Record<
  EventsBoxContext,
  {
    securityCountry: ISecurityCountry;
    currency: "USD" | "BRL";
    hasCalendar: boolean;
    hasReleases: boolean;
    hasStockDividends: boolean;
    filterDividendsByTickerSymbol: boolean;
    formatStockDividendsAsPercentage: boolean;
  }
> = {
  STOCKS: {
    securityCountry: ISecurityCountry.Us,
    currency: "USD",
    hasCalendar: true,
    hasReleases: false,
    hasStockDividends: true,
    filterDividendsByTickerSymbol: false,
    formatStockDividendsAsPercentage: false,
  },
  ACOES: {
    securityCountry: ISecurityCountry.Br,
    currency: "BRL",
    hasCalendar: true,
    hasReleases: true,
    hasStockDividends: true,
    filterDividendsByTickerSymbol: true,
    formatStockDividendsAsPercentage: true,
  },
  FIIS: {
    securityCountry: ISecurityCountry.Br,
    currency: "BRL",
    hasCalendar: false,
    hasReleases: false,
    hasStockDividends: false,
    filterDividendsByTickerSymbol: true,
    formatStockDividendsAsPercentage: false,
  },
};

interface EventsBoxProps extends Partial<EventsCalendarState> {
  tickerSymbol: string;
  context: EventsBoxContext;
  isRenderPageDefault?: boolean;
  tab: CompanyEventsTabEnum;
  onChangeTab: (tab: CompanyEventsTabEnum) => void;
  corporateActionsTickerSymbols?: string[];
  issuerType?: IIssuerType;
}

const CONTEXT_TO_ISSUER_TYPE: Record<EventsBoxContext, IIssuerType> = {
  STOCKS: IIssuerType.AmericanCompany,
  ACOES: IIssuerType.BrazilianCompany,
  FIIS: IIssuerType.BrazilianRealEstateFund,
};

export default function EventsBox({
  tickerSymbol,
  context,
  isRenderPageDefault = false,
  tab,
  onChangeTab,
  corporateActionsTickerSymbols,
  issuerType,
  ...filterState
}: EventsBoxProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);
  const [enabledFullHeight, setEnabledFullHeight] = useState(false);

  const config = CONTEXT_CONFIG[context];

  const { data: globalCorporateActionsData, loading: globalCorporateActionsLoading } = useQuery<
    IGlobalCorporateActionsQuery,
    IGlobalCorporateActionsQueryVariables
  >(GlobalCorporateActionsQuery, {
    variables: {
      tickerSymbols: corporateActionsTickerSymbols ?? [tickerSymbol],
      securityCountry: ISecurityCountry.Br,
    },
  });

  const {
    data: currentIssuerDividendsData,
    previousData: previousIssuerDividendsData,
    loading: issuerDividendsLoading,
  } = useQuery<IIssuerDividendsQuery, IIssuerDividendsQueryVariables>(IssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: config.securityCountry,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  const issuerDividendsData =
    currentIssuerDividendsData && currentIssuerDividendsData ? currentIssuerDividendsData : previousIssuerDividendsData;

  const hasDividends = !!issuerDividendsData?.dividendsPaid.nodes?.length;
  const hasStockDividends = !!(config.hasStockDividends && issuerDividendsData?.stockDividendsPaid?.length);
  const hasStockSplit = !!globalCorporateActionsData?.stockSplit?.length;
  const hasRightsIssues = !!globalCorporateActionsData?.rightsIssues?.length;

  const dividendsData = config.filterDividendsByTickerSymbol
    ? issuerDividendsData?.dividendsPaid.nodes.filter((node) => node.tickerSymbol === tickerSymbol)
    : issuerDividendsData?.dividendsPaid.nodes;

  const dividendYieldData = config.filterDividendsByTickerSymbol
    ? issuerDividendsData?.dividendsPaid.dividendYieldByTickerSymbol.filter(
        (node) => node.tickerSymbol === tickerSymbol,
      )
    : issuerDividendsData?.dividendsPaid.dividendYieldByTickerSymbol;

  const tabs = useMemo(() => {
    if (!viewer?.isPremium) {
      return [
        {
          value: CompanyEventsTabEnum.DIVIDENDS,
          label: intl.formatMessage({
            defaultMessage: "Dividendos",
            description: "Aba do menu superior da empresa",
            id: "cesSrh",
          }),
          icon: <HandCoinsIcon className="inline-flex" size={16} strokeWidth={2} />,
          disabled: !hasDividends,
        },
      ];
    }

    return [
      config.hasCalendar
        ? {
            value: CompanyEventsTabEnum.CALENDAR,
            label: intl.formatMessage({
              defaultMessage: "Calendário",
              description: "Aba do menu superior da empresa",
              id: "GiXunu",
            }),
            icon: <CalendarDaysIcon className="inline-flex" size={16} strokeWidth={2} />,
          }
        : null,
      config.hasReleases
        ? {
            value: CompanyEventsTabEnum.RELEASES,
            label: intl.formatMessage({
              defaultMessage: "Comunicados",
              description: "Aba do menu superior da empresa",
              id: "cZ66Fi",
            }),
            icon: <MegaphoneIcon className="inline-flex" size={16} strokeWidth={2} />,
          }
        : null,
      {
        value: CompanyEventsTabEnum.DIVIDENDS,
        label: intl.formatMessage({
          defaultMessage: "Dividendos",
          description: "Aba do menu superior da empresa",
          id: "cesSrh",
        }),
        icon: <HandCoinsIcon className="inline-flex" size={16} strokeWidth={2} />,
        disabled: !hasDividends,
      },
      config.hasStockDividends
        ? {
            value: CompanyEventsTabEnum.STOCK_DIVIDENDS,
            label: intl.formatMessage({
              defaultMessage: "Bonificação",
              description: "Aba do menu superior da empresa",
              id: "XVUdfX",
            }),
            icon: <CirclePercentIcon className="inline-flex" size={16} strokeWidth={2} />,
            disabled: !hasStockDividends,
          }
        : null,
      {
        value: CompanyEventsTabEnum.RIGHTS_ISSUES,
        label: intl.formatMessage({
          defaultMessage: "Subscrição",
          description: "Aba do menu superior da empresa",
          id: "+HliZT",
        }),
        icon: <TicketPercentIcon className="inline-flex" size={16} strokeWidth={2} />,
        disabled: !hasRightsIssues,
      },
      {
        value: CompanyEventsTabEnum.STOCK_SPLIT,
        label: intl.formatMessage({
          defaultMessage: "Desdobramento",
          description: "Aba do menu superior da empresa",
          id: "VZYL3m",
        }),
        icon: <SquareSplitHorizontalIcon className="inline-flex" size={16} strokeWidth={2} />,
        disabled: !hasStockSplit,
      },
    ].filter(notUndefinedOrNull);
  }, [viewer?.isPremium, config, hasDividends, hasStockDividends, hasStockSplit, hasRightsIssues, intl]);

  return (
    <CompanyContainer
      className={classNames("xl:h-fit", {
        "xl:h-full xl:min-h-company-box": enabledFullHeight,
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyEventsTabEnum.DIVIDENDS}
          value={viewer?.isPremium ? tab : CompanyEventsTabEnum.DIVIDENDS}
          onValueChange={(value) => {
            onChangeTab(value as CompanyEventsTabEnum);
            // TODO: Fix this later on. Might not be necessary.
            setEnabledFullHeight(false);
          }}
          className="h-auto w-full"
        >
          <CompanyTabsList tabs={tabs} />

          {viewer?.isPremium && config.hasCalendar && (
            <CompanyTabsContent value={CompanyEventsTabEnum.CALENDAR} className="py-4">
              <div className="w-full">
                <CalendarContent
                  {...(filterState as EventsCalendarState)}
                  tickerSymbol={tickerSymbol}
                  isRenderPageDefault={isRenderPageDefault}
                  issuerType={issuerType ?? CONTEXT_TO_ISSUER_TYPE[context]}
                />
              </div>
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && config.hasReleases && (
            <CompanyTabsContent value={CompanyEventsTabEnum.RELEASES}>
              <BrazilianIpesContent isRenderPageDefault={isRenderPageDefault} />
            </CompanyTabsContent>
          )}

          <CompanyTabsContent value={CompanyEventsTabEnum.DIVIDENDS}>
            {issuerDividendsData && issuerDividendsData.dividendsPaid && (
              <DividendsContent
                tickerSymbol={tickerSymbol}
                currency={config.currency}
                loading={issuerDividendsLoading}
                filterDividendsByYear={filterDividendsByYear}
                setFilterDividendsByYear={setFilterDividendsByYear}
                availableYears={issuerDividendsData.dividendsPaid.availableYears}
                data={dividendsData || []}
                dividendYieldSnapshotEachSymbol={dividendYieldData || []}
                isRenderPageDefault={isRenderPageDefault}
                setEnabledFullHeight={setEnabledFullHeight}
              />
            )}
          </CompanyTabsContent>

          {viewer?.isPremium && config.hasStockDividends && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_DIVIDENDS}>
              <StockDividendsContent
                data={(issuerDividendsData && issuerDividendsData.stockDividendsPaid) || []}
                formatAsPercentage={config.formatStockDividendsAsPercentage}
                isRenderPageDefault={isRenderPageDefault}
              />
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.RIGHTS_ISSUES}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <RightsIssuesContent
                  data={globalCorporateActionsData?.rightsIssues || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_SPLIT}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <StockSplitContent
                  data={globalCorporateActionsData?.stockSplit || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
